import { CloseOutlined, PaperClipOutlined } from '@ant-design/icons'
import useCarers from '@features/organisation/hooks/useCarers'
import type { ResidentItem } from '@lmc/ts-common'
import {
  EmptyState,
  ExpandableCard,
  type ExpandableCardField,
  NavigationModal,
  NoDataIcon,
  PageContentWrapper,
  Spinner
} from '@lmc/ui-kit'
import type { MenuProps } from 'antd'
import { useAbilityAccess } from 'components/AccessControl'
import { NAMESPACE } from 'locale'
import { residentHealthRecordsConstants } from 'locale/residentHealthRecords'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useCommunicationRecordDocumentRemove } from '../hooks/useCommunicationRecordDocumentRemove'
import { useCommunicationRecordsList } from '../hooks/useCommunicationRecordsList'
import {
  formatDate,
  getStatusLabel,
  getStatusTagColor
} from '../utils/communicationRecordUtils'
import type { CommunicationRecord } from '../utils/types'

type MenuInfo = Parameters<NonNullable<MenuProps['onClick']>>[0]

type CommunicationRecordListProps = {
  residentId: string
  resident?: ResidentItem
  onEditRecord?: (record: CommunicationRecord) => void
  onDeleteRecord?: (record: CommunicationRecord) => void
}

const CommunicationRecordList = ({
  residentId,
  resident,
  onEditRecord,
  onDeleteRecord
}: CommunicationRecordListProps) => {
  const { t } = useTranslation(NAMESPACE.RESIDENT_HEALTH_RECORDS)
  const { communicationRecords, isLoading, refetch } =
    useCommunicationRecordsList({
      residentId,
      enabled: !!residentId
    })
  const { removeDocument, isRemoving } = useCommunicationRecordDocumentRemove({
    onSuccess: () => {
      refetch()
    }
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [recordToDelete, setRecordToDelete] =
    useState<CommunicationRecord | null>(null)
  const [expandedRecordId, setExpandedRecordId] = useState<string | null>(null)
  const [showRemoveDocModal, setShowRemoveDocModal] = useState(false)
  const [docToRemove, setDocToRemove] = useState<{
    recordId: string
    documentId: string
    filename: string
  } | null>(null)
  const { carers } = useCarers()

  const canDeleteCommunicationRecords = useAbilityAccess([
    {
      i: 'delete',
      this: 'CommunicationRecord'
    }
  ])

  const canEditCommunicationRecords = useAbilityAccess([
    {
      i: 'update',
      this: 'CommunicationRecord'
    }
  ])

  if (isLoading) {
    return <Spinner style={{ minHeight: 200 }} />
  }

  if (!communicationRecords || communicationRecords.length === 0) {
    return (
      <EmptyState
        icon={<NoDataIcon />}
        title={t(residentHealthRecordsConstants.NO_COMMUNICATION_RECORDS)}
        description={t(
          residentHealthRecordsConstants.NO_COMMUNICATION_RECORDS_DESCRIPTION
        )}
      />
    )
  }

  const getActionItems = (record: CommunicationRecord): MenuProps['items'] => {
    const items: MenuProps['items'] = []

    if (canEditCommunicationRecords) {
      items.push({
        key: 'edit',
        label: t(residentHealthRecordsConstants.EDIT),
        onClick: (e: MenuInfo) => {
          e.domEvent?.stopPropagation()
          onEditRecord?.(record)
        }
      })
    }

    // Documents are shown inline in expanded view, no need for menu item

    if (canDeleteCommunicationRecords) {
      items.push({
        key: 'delete',
        label: t(residentHealthRecordsConstants.DELETE),
        onClick: (e: MenuInfo) => {
          e.domEvent?.stopPropagation()
          setRecordToDelete(record)
          setShowDeleteModal(true)
        },
        danger: true
      })
    }

    return items
  }

  const toggleExpand = (recordId: string) => {
    setExpandedRecordId(expandedRecordId === recordId ? null : recordId)
  }

  const getCarerName = (carerId?: string) => {
    if (!carerId || !carers) return null
    const carer = carers.find((c) => c.id === carerId)
    if (!carer) return null
    return `${carer.name.first} ${carer.name.last}`
  }

  const getRecipientName = (recipientId?: string) => {
    if (!recipientId || !resident?.importantContacts) return null
    const contact = resident.importantContacts.find(
      (c) => (c as unknown as { id: string }).id === recipientId
    )
    if (!contact) return null
    return `${contact.name.first} ${contact.name.last}`
  }

  const handleConfirmDelete = () => {
    if (recordToDelete) {
      onDeleteRecord?.(recordToDelete)
      setShowDeleteModal(false)
      setRecordToDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setRecordToDelete(null)
  }

  const buildFields = (record: CommunicationRecord): ExpandableCardField[] => {
    const fields: ExpandableCardField[] = []
    const sentByName = getCarerName(record.sentBy)
    const recipientName = getRecipientName(record.recipient)

    if (record.description) {
      fields.push({
        label: t(residentHealthRecordsConstants.COMMUNICATION_DESCRIPTION),
        value: record.description,
        fullWidth: true
      })
    }

    if (sentByName) {
      fields.push({
        label: t(residentHealthRecordsConstants.SENT_BY),
        value: sentByName
      })
    }

    if (recipientName) {
      fields.push({
        label: t(residentHealthRecordsConstants.RECIPIENT_LABEL),
        value: recipientName
      })
    }

    // Show multiple documents if they exist
    if (record.documents && record.documents.length > 0) {
      const documents = record.documents
      const documentLinks = documents.map((doc: any, index: number) => {
        const docId = doc._id || doc.id
        const filename = doc.filename || doc.name || `Document ${index + 1}`

        return (
          <DocumentRow
            key={index}
            style={{
              marginBottom: index < documents.length - 1 ? '4px' : 0
            }}
          >
            <DocumentLink
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {filename}
            </DocumentLink>
            {canEditCommunicationRecords && (
              <RemoveButton
                onClick={(e) => {
                  e.stopPropagation()
                  setDocToRemove({
                    recordId: record.id || (record as any)._id,
                    documentId: docId,
                    filename
                  })
                  setShowRemoveDocModal(true)
                }}
                title="Remove document"
              >
                <CloseOutlined />
              </RemoveButton>
            )}
          </DocumentRow>
        )
      })

      fields.push({
        label: t(residentHealthRecordsConstants.DOCUMENT_UPLOAD),
        value: <>{documentLinks}</>,
        fullWidth: true
      })
    }

    return fields
  }

  const renderRecord = (record: CommunicationRecord) => {
    const menuItems = getActionItems(record)
    const fields = buildFields(record)
    const statusTagColor = getStatusTagColor(record.communicationStatus)
    const recordId = record.id || (record as any)._id || ''

    return (
      <ExpandableCard
        key={recordId}
        id={recordId}
        title={record.title}
        status={{
          label: t(getStatusLabel(record.communicationStatus)),
          color: statusTagColor
        }}
        date={
          record.dateSent ? `Sent: ${formatDate(record.dateSent)}` : undefined
        }
        titleIndicators={
          record.documents && record.documents.length > 0 ? (
            <AttachmentIcon
              title={t(residentHealthRecordsConstants.HAS_ATTACHMENT)}
            >
              <PaperClipOutlined />
              {record.documents.length > 1 && (
                <span style={{ marginLeft: '4px' }}>
                  ({record.documents.length})
                </span>
              )}
            </AttachmentIcon>
          ) : undefined
        }
        fields={fields}
        menuItems={menuItems}
        isExpanded={expandedRecordId === recordId}
        onToggleExpand={fields.length > 0 ? toggleExpand : undefined}
        testId={`communication-record-${recordId}`}
      />
    )
  }

  const handleConfirmRemoveDocument = async () => {
    if (!docToRemove) return

    try {
      await removeDocument(docToRemove.recordId, docToRemove.documentId)
      setShowRemoveDocModal(false)
      setDocToRemove(null)
    } catch (error) {
      console.error('Failed to remove document:', error)
    }
  }

  const handleCancelRemoveDocument = () => {
    setShowRemoveDocModal(false)
    setDocToRemove(null)
  }

  return (
    <>
      <ListContainer>{communicationRecords.map(renderRecord)}</ListContainer>
      <NavigationModal
        showPrompt={showDeleteModal}
        modalTitle={t(
          residentHealthRecordsConstants.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_COMMUNICATION_RECORD
        )}
        modalContentText={t(
          residentHealthRecordsConstants.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_COMMUNICATION_RECORD_DESCRIPTION
        )}
        modalConfirmText={t(residentHealthRecordsConstants.DELETE)}
        modalCancelText={t(residentHealthRecordsConstants.CANCEL)}
        confirmNavigation={handleConfirmDelete}
        cancelNavigation={handleCancelDelete}
      />
      <NavigationModal
        showPrompt={showRemoveDocModal}
        modalTitle="Remove Document"
        modalContentText={`Are you sure you want to remove "${docToRemove?.filename}"? This action cannot be undone.`}
        modalConfirmText="Remove"
        modalCancelText="Cancel"
        confirmNavigation={handleConfirmRemoveDocument}
        cancelNavigation={handleCancelRemoveDocument}
      />
    </>
  )
}

export default CommunicationRecordList

const ListContainer = styled(PageContentWrapper).attrs({
  $gap: 12,
  $paddingBottom: 40
})`
  margin-top: 24px;
`

const AttachmentIcon = styled.span`
  color: ${({ theme }) => theme.color?.neutral?.color5 || '#999999'};
  font-size: 14px;
`

const DocumentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const DocumentLink = styled.a`
  font-size: 14px;
  color: ${({ theme }) => theme.color?.status?.primary || '#1890FF'};
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.color?.neutral?.color5 || '#8c8c8c'};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.color?.status?.danger || '#ff4d4f'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
